import { defineStore, storeToRefs } from 'pinia'
import { Ref, ref } from 'vue'
import {
  NyUngdom,
  OtHurtigvalgUngdom,
  OtDeltUngdom,
  BulkUngdom,
  UngdomPersonopplysninger,
  Ungdom,
  UngdomsListeFilterType
} from '../models/types'
import { OtEnhet, Saksbehandler, Skolear } from '@/modules/core/models/types'
import { otUngdomClient } from '@/openapi/clients'
import { eventBus } from '@/plugins/eventBus'
import { useI18n } from 'vue-i18n'
import router from '@/router'
import { cached } from '@/helpers/cache'
import { createGroup, FilterJoin } from '@/modules/core/filter/models/filter'
import { useCoreStore } from '@/modules/core/store'

export const useUngdommerStore = defineStore('ungdommerStore', () => {
  const ungdommer = ref<Ungdom[]>([] as Ungdom[])
  const ungdomListeFilterStandard = ref<UngdomsListeFilterType>()
  const ungdomListeFilterBrukt = ref<UngdomsListeFilterType>()
  const valgtUngdom = ref<Ungdom | undefined>({} as Ungdom)
  const ungdomPersonOpplysninger = ref<UngdomPersonopplysninger>({} as UngdomPersonopplysninger)
  const lasterUngdommer = ref<boolean>(false)
  const lasterValgtUngdom = ref<boolean>(false)
  const lasterUngdomPersonopplysninger = ref<boolean>(false)
  const nyUngdom = ref<NyUngdom | undefined>({} as NyUngdom)
  const nyUngdomFeilmelding = ref<string>('')
  const hurtigvalgte = ref<OtHurtigvalgUngdom[]>([] as OtHurtigvalgUngdom[])
  const cachedHurtigvalgte = ref<OtHurtigvalgUngdom[]>()
  const delteUngdommer = ref<OtDeltUngdom[] | undefined>()
  const systemGeneratedJournalCount = ref<number>(0)

  const { aktivtSkolear, mineEnheter, mineSaksbehandlere, brukerId } = storeToRefs(useCoreStore())

  const { t } = useI18n()

  async function hentUngdommer(
    skolear: Skolear,
    enheter: OtEnhet[],
    saksbehandlere: Saksbehandler[] | undefined,
    deltMed?: boolean,
    deltAv?: boolean,
    jsonFilter?: string,
    nullSaksbehandlere?: boolean
  ) {
    lasterUngdommer.value = true

    const enhetsIds = enheter?.map((enhet) => enhet.id) ?? []

    let brukerIds = saksbehandlere?.map((val) => val.id)
    if (nullSaksbehandlere) {
      if (!brukerIds) {
        brukerIds = ['null']
      } else {
        brukerIds.push('null')
      }
    }

    const query = {
      skolear: skolear.kode,
      enhet_ids: enhetsIds,
      bruker_ids: brukerIds,
      delt_med: deltMed,
      delt_av: deltAv,
      filter: jsonFilter
    }
    try {
      const { data, error } = await otUngdomClient.POST('/v1/ot-ungdommer/hent-ved-post', {
        body: query
      })
      if (data && data.ungdommer) {
        ungdommer.value = data.ungdommer
        Promise.resolve(data)
      }
      if (error) {
        eventBus.$emit('showError', t('ungdommer.hent.feil'))
        Promise.reject(error)
        return
      }
    } finally {
      lasterUngdommer.value = false
    }
  }

  async function hentUngdom(id: string, skolear: string) {
    lasterValgtUngdom.value = true

    try {
      const fromList = ungdommer.value.find((u: Ungdom) => u.id === id)

      if (!fromList) {
        const { data, error } = await otUngdomClient.GET('/v1/ot-ungdommer/{ungdom_id}', {
          params: {
            path: { ungdom_id: id },
            query: { skolear: skolear }
          }
        })
        if (error) {
          valgtUngdom.value = undefined
          Promise.reject(error)
          return
        }
        valgtUngdom.value = data
      } else {
        valgtUngdom.value = fromList
      }
    } finally {
      lasterValgtUngdom.value = false
    }
  }

  async function hentHurtigvalg() {
    if (aktivtSkolear.value?.kode) {
      await cached(cachedHurtigvalgte, async () => {
        const { data, error } = await otUngdomClient.GET('/v1/ot-ungdom-hurtigvalg', {
          params: {
            query: {
              skolear: aktivtSkolear.value.kode
            }
          }
        })
        if (data) {
          if (data.hurtigvalg) {
            cachedHurtigvalgte.value = data.hurtigvalg.sort((a, b) => {
              const navnA = a.ungdom_navn || ''
              const navnB = b.ungdom_navn || ''
              return navnA.localeCompare(navnB)
            })
          }
          if (error) {
            eventBus.$emit('showError', t('ungdommer.personopplysninger.hurtigvalg.hent.feil'))
          }
        }
      })
      hurtigvalgte.value = cachedHurtigvalgte.value ?? []
    }
  }

  function isHurtigvalg(ungdom_id: string) {
    const hurtigvalg = hurtigvalgte?.value?.find((s) => s.ungdom_id === ungdom_id) ?? undefined
    return hurtigvalg !== undefined
  }

  async function settHurtigvalg(ungdom_id: string) {
    const { data, error } = await otUngdomClient.PUT('/v1/ot-ungdom-hurtigvalg', {
      body: {
        ungdom_id
      }
    })
    if (data) {
      hurtigvalgte.value.push(data)
      hurtigvalgte.value.sort((a, b) => {
        const navnA = a.ungdom_navn || ''
        const navnB = b.ungdom_navn || ''
        return navnA.localeCompare(navnB)
      })
      eventBus.$emit('showSuccess', t('ungdommer.personopplysninger.hurtigvalg.opprett.ok'))
    }
    if (error) {
      eventBus.$emit('showError', t('ungdommer.personopplysninger.hurtigvalg.opprett.feil'))
    }
  }

  async function slettHurtigvalg(ungdom_id: string) {
    const hurtigvalg = hurtigvalgte?.value?.find((s) => s.ungdom_id === ungdom_id)
    if (hurtigvalg?.id) {
      const { response } = await otUngdomClient.DELETE('/v1/ot-ungdom-hurtigvalg/{id}', {
        params: {
          path: { id: hurtigvalg.id }
        }
      })
      if (response.ok) {
        const idx = hurtigvalgte.value.findIndex((hurtigvalg) => hurtigvalg.ungdom_id === ungdom_id)
        if (idx > -1) {
          hurtigvalgte.value.splice(idx, 1)
        }
        eventBus.$emit('showSuccess', t('ungdommer.personopplysninger.hurtigvalg.slett.ok'))
      } else {
        eventBus.$emit('showError', t('ungdommer.personopplysninger.hurtigvalg.slett.feil'))
      }
    }
  }

  async function hentUngdomPersonopplysninger(ungdomId: string, skolear: string) {
    lasterUngdomPersonopplysninger.value = true

    try {
      const { data, error } = await otUngdomClient.GET(
        '/v1/ot-ungdommer/{ungdom_id}/{skolear}/personopplysninger',
        {
          params: {
            path: { ungdom_id: ungdomId, skolear: skolear }
          }
        }
      )
      if (error) {
        eventBus.$emit('showError', 'Henting av personopplysninger feilet')
        Promise.reject(error)
      } else {
        ungdomPersonOpplysninger.value = data
      }
    } finally {
      lasterUngdomPersonopplysninger.value = false
    }
  }

  async function lagreUngdomPersonopplysninger(
    lagreUngdomOpplysninger: UngdomPersonopplysninger,
    skolear: Skolear
  ): Promise<boolean> {
    const { data, error } = await otUngdomClient.POST(
      '/v1/ot-ungdommer/{ungdom_id}/personopplysninger',
      {
        params: {
          path: {
            ungdom_id: lagreUngdomOpplysninger.elev2_id!
          }
        },
        body: {
          skolear: skolear.kode,
          morsmal_id: lagreUngdomOpplysninger.morsmal?.id,
          epost: lagreUngdomOpplysninger.epost,
          mobil: lagreUngdomOpplysninger.mobil,
          ot_avtalt_tid: lagreUngdomOpplysninger.ot_avtalt_tid,
          ot_enhet_id: lagreUngdomOpplysninger.ot_enhet?.id,
          saksbehandler_id: lagreUngdomOpplysninger.saksbehandler?.id,
          ot_merknad_ot: lagreUngdomOpplysninger.ot_merknad_ot,
          bosted_adresse: lagreUngdomOpplysninger.ungdom_adresse?.bosteds_adresse,
          foresatt1: lagreUngdomOpplysninger.ungdom_foresatte?.foresatt1,
          foresatt2: lagreUngdomOpplysninger.ungdom_foresatte?.foresatt2
        }
      }
    )
    if (error) {
      eventBus.$emit('showError', 'Oppdatering av personopplysninger feilet')
      return false
    } else {
      ungdomPersonOpplysninger.value = data
      eventBus.$emit('showSuccess', 'Oppdatering av personopplysninger var vellykket')
      systemGeneratedJournalCount.value++
      return true
    }
  }

  async function lagreNyUngdom(nyUngdomParam: NyUngdom) {
    const { data, error } = await otUngdomClient.POST('/v1/ot-ungdommer', {
      body: nyUngdomParam
    })
    if (data) {
      await router.push(`ungdommer/ungdom/${data.elev2_id}`)
      eventBus.$emit('showSuccess', t('ungdommer.personopplysninger.nyUngdom.ok'))

      const idx = ungdommer.value.findIndex((ungdom) => ungdom.elev2_id === data.elev2_id)

      if (idx > -1) {
        ungdommer.value.splice(idx, 1, data)
      } else {
        ungdommer.value.unshift(data)
      }
    }
    if (error) {
      const feilkode = error.errors[0]?.code
      let feiltekst = error.errors[0]?.message
      if (feiltekst) {
        nyUngdomFeilmelding.value = feiltekst
      }

      let feilmelding
      if (feilkode === 'UNGDOM_EKSISTERER') {
        const params = error.errors[0]!.parameters!
        const enhet = params[0]!
        const skolear = params[1]!
        const navn = params[2]! + ' ' + params[3]!
        feiltekst = t('ungdommer.personopplysninger.nyUngdom.endre', [enhet, navn, skolear])
        nyUngdomFeilmelding.value = feiltekst

        nyUngdom.value = nyUngdomParam
        nyUngdom.value.bytt_enhet_eller_saksbehandler = true
        return
      } else if (feilkode === 'ELEV2_FINNES_IKKE') {
        feilmelding = t('ungdommer.personopplysninger.nyUngdom.finnesIkke')
      } else if (feilkode === 'UNGDOM_FEIL_ALDER') {
        const params = error.errors[0]!.parameters!
        const otAlder = params[0]

        feilmelding = t('ungdommer.personopplysninger.nyUngdom.forGammel', [otAlder])
      } else if (feilkode === 'UNGDOM_FEIL_BYDEL') {
        const params = error.errors[0]!.parameters!
        feilmelding = t('ungdommer.personopplysninger.nyUngdom.feilBydel', params)
      } else {
        feilmelding = t('ungdommer.personopplysninger.nyUngdom.feil')
      }
      eventBus.$emit('showError', feilmelding)
    }
  }

  async function setStandardUngdomListeFilter(
    skolear: Skolear,
    enheter: OtEnhet[],
    saksbehandler: Saksbehandler
  ) {
    const filter: UngdomsListeFilterType = {
      skolear: skolear,
      enheter: enheter,
      saksbehandlere: [saksbehandler],
      filters: new Map(),
      hentet: false,
      filterKonfigurasjon: createGroup(FilterJoin.AND),
      utenSaksbehandler: false,
      alleSaksbehandlere: false,
      alleEnheter: true
    }
    ungdomListeFilterStandard.value = filter
    ungdomListeFilterBrukt.value = filter
  }

  function ungdommerOppdatert(oppdaterteUngdommer: Ungdom[]) {
    oppdaterteUngdommer.forEach((oppdatert) => {
      const idx = ungdommer.value.findIndex(
        (u) => u.id === oppdatert.id && u.skolear === oppdatert.skolear
      )
      if (idx > -1) {
        ungdommer.value.splice(idx, 1, oppdatert)
      }
    })
  }

  async function oppdaterSaksbehandler(bulkUngdom: BulkUngdom) {
    const { data, error } = await otUngdomClient.POST('/v1/bulk/ot-ungdommer', {
      body: bulkUngdom
    })

    if (data) {
      data.ungdommer?.forEach((ungdom) => {
        const idx = ungdommer.value.findIndex((u) => u.id === ungdom.id)
        if (idx > -1 && ungdommer.value[idx]!.saksbehandler?.id !== ungdom.saksbehandler?.id) {
          ungdommer.value.splice(idx, 1)
        }
      })
      eventBus.$emit('showSuccess', t('ungdommer.personopplysninger.oppdaterSaksbehandler.ok'))
    }
    if (error) {
      eventBus.$emit('showError', t('ungdommer.personopplysninger.oppdaterSaksbehandler.feil'))
    }

    return data?.ungdommer
  }

  async function lagreNyDeltUngdom(ungdom_id: string, delt_med_id: string) {
    const { data } = await otUngdomClient.PUT('/v1/ot-delte-ungdommer', {
      body: {
        ungdom_id: ungdom_id,
        delt_med_id: delt_med_id,
        skolear: aktivtSkolear.value?.kode
      }
    })
    if (data) {
      if (delteUngdommer.value) {
        delteUngdommer.value.unshift(data)
      } else {
        delteUngdommer.value = [data]
      }
    } else {
      throw new Error('Feil ved oppretting av delt ungdom')
    }
  }

  async function slettDeltUngdom(deltUngdom: OtDeltUngdom) {
    const { response } = await otUngdomClient.DELETE('/v1/ot-delte-ungdommer/{id}', {
      params: {
        path: { id: deltUngdom.id }
      }
    })
    if (!response.ok) {
      throw new Error('Feil ved sletting av delt ungdom')
    }

    if (delteUngdommer.value) {
      delteUngdommer.value.splice(delteUngdommer.value?.indexOf(deltUngdom), 1)
    }
  }

  async function erstattDeling(ungdom_id: string, nyeDeltMedIder: string[]) {
    try {
      const deltMedIder =
        delteUngdommer.value
          ?.filter((ungdom) => ungdom.ungdom_id === ungdom_id)
          .map((deling) => deling.delt_med.id) ?? []

      const skalOpprettes = nyeDeltMedIder.filter((id) => !deltMedIder.includes(id))
      const skalSlettes =
        delteUngdommer.value?.filter((ungdom) => !nyeDeltMedIder.includes(ungdom.delt_med.id)) ?? []

      for (const behandlerId of skalOpprettes) {
        await lagreNyDeltUngdom(ungdom_id, behandlerId)
      }

      for (const ungdom of skalSlettes) {
        await slettDeltUngdom(ungdom)
      }
      if (skalOpprettes.length > 0 && skalSlettes.length === 0) {
        eventBus.$emit(
          'showSuccess',
          t('ungdommer.personopplysninger.deltUngdom.opprett.ok', skalOpprettes.length)
        )
      } else if (skalSlettes.length > 0 && skalOpprettes.length === 0) {
        eventBus.$emit(
          'showSuccess',
          t('ungdommer.personopplysninger.deltUngdom.slettet.ok', skalSlettes.length)
        )
      } else if (skalOpprettes.length > 0 || skalSlettes.length > 0) {
        eventBus.$emit('showSuccess', t('ungdommer.personopplysninger.deltUngdom.endret.ok'))
      }
      if (skalOpprettes.length > 0 || skalSlettes.length > 0) {
        systemGeneratedJournalCount.value++
      }
    } catch (_error) {
      eventBus.$emit('showError', t('ungdommer.personopplysninger.deltUngdom.endret.feil'))
    }
  }

  async function hentDelteUngdommer() {
    return cached(delteUngdommer, () => doHentDelteUngdommer())
  }

  async function doHentDelteUngdommer() {
    const { data, error } = await otUngdomClient.GET('/v1/ot-delte-ungdommer', {
      params: {
        query: {
          delt_av: true,
          delt_med: true
        }
      }
    })
    if (data && data.ungdommer) {
      delteUngdommer.value = data.ungdommer
      Promise.resolve(data.ungdommer)
    }
    if (error) {
      Promise.reject(error)
      return
    }
  }

  function brukerHarTilgangTilDeltUngdom(ungdomId: string) {
    return (
      delteUngdommer.value?.some(
        (deltUngdom) =>
          deltUngdom.ungdom_id === ungdomId && deltUngdom.delt_med.id === brukerId.value
      ) ?? false
    )
  }

  function brukerHarTilgangTilUngdom(ungdomId: string, enhetId: string): boolean {
    if (!ungdomId || !enhetId) {
      return false
    }

    const harEnhetTilgang = !!enhetId && mineEnheter.value.some((enhet) => enhet.id === enhetId)

    const harDeltTilgang = brukerHarTilgangTilDeltUngdom(ungdomId)

    return harEnhetTilgang || harDeltTilgang
  }

  async function slettUngdom(ungdomId: string) {
    const { response } = await otUngdomClient.DELETE('/v1/ot-ungdommer/{ungdom_id}', {
      params: {
        path: { ungdom_id: ungdomId }
      }
    })
    if (response.ok) {
      const idx = ungdommer.value.findIndex((ungdom) => ungdom.id === ungdomId)
      if (idx > -1) {
        ungdommer.value.splice(idx, 1)
      }
      if (valgtUngdom.value?.id === ungdomId) {
        valgtUngdom.value = undefined
      }
      eventBus.$emit('showSuccess', t('ungdommer.slett.ok'))
      router.push({ name: 'Ungdommer' })
    } else {
      eventBus.$emit('showError', t('ungdommer.slett.feil'))
    }
  }

  function resetToStandardFilter() {
    const meg = mineSaksbehandlere.value?.find(
      (saksbehandler: Saksbehandler) => brukerId.value === saksbehandler.id
    )
    setStandardUngdomListeFilter(aktivtSkolear.value, mineEnheter.value, meg!)
  }

  function setSkolearFraHurtigvalg(skolearParam: Skolear) {
    eventBus.$emit('showSuccess', t('app_bar.skolearHurtigvalg.endre.ok', [skolearParam.navn]))
    aktivtSkolear.value = skolearParam
    cachedHurtigvalgte.value = undefined
    resetToStandardFilter()
  }

  async function hentMedStandardFilterHvisUngdommerIkkeHentet() {
    if (!ungdomListeFilterBrukt.value?.hentet) {
      resetToStandardFilter()
      ungdomListeFilterBrukt.value!.hentet = true
      await hentUngdommer(
        aktivtSkolear.value,
        ungdomListeFilterBrukt.value?.enheter ?? [],
        ungdomListeFilterBrukt.value?.saksbehandlere ?? []
      )
    }
  }

  async function apneHistorikk(ungdomPersonOpplysninger: Ref<UngdomPersonopplysninger>) {
    const { data, error } = await otUngdomClient.PUT(
      '/v1/ot-ungdommer/{ungdom_id}/historikk_apning/{skolear}',
      {
        params: {
          path: {
            ungdom_id: ungdomPersonOpplysninger.value.elev2_id!,
            skolear: aktivtSkolear.value.kode!
          }
        }
      }
    )
    if (data) {
      ungdomPersonOpplysninger.value.historikk_apen = true
      ungdomPersonOpplysninger.value.historikk_apning = data
      eventBus.$emit('showSuccess', t('ungdommer.personopplysninger.historikk.apne.ok'))
      systemGeneratedJournalCount.value++
    }
    if (error) {
      eventBus.$emit('showError', t('ungdommer.personopplysninger.historikk.apne.feil'))
    }
  }

  async function lukkHistorikk(ungdomPersonOpplysninger: Ref<UngdomPersonopplysninger>) {
    const { response } = await otUngdomClient.DELETE(
      '/v1/ot-ungdommer/{ungdom_id}/historikk_apning/{skolear}',
      {
        params: {
          path: {
            ungdom_id: ungdomPersonOpplysninger.value.elev2_id!,
            skolear: aktivtSkolear.value.kode!
          }
        }
      }
    )
    if (response.ok) {
      ungdomPersonOpplysninger.value.historikk_apen = false
      ungdomPersonOpplysninger.value.historikk_apning = undefined
      eventBus.$emit('showSuccess', t('ungdommer.personopplysninger.historikk.lukk.ok'))
      systemGeneratedJournalCount.value++
    } else {
      eventBus.$emit('showError', t('ungdommer.personopplysninger.historikk.lukk.feil'))
    }
  }

  return {
    ungdommer,
    valgtUngdom,
    ungdomPersonOpplysninger,
    lasterValgtUngdom,
    lasterUngdommer,
    lasterUngdomPersonopplysninger,
    hentHurtigvalg,
    hentUngdommer,
    hentMedStandardFilterHvisUngdommerIkkeHentet,
    hentUngdom,
    hentUngdomPersonopplysninger,
    lagreUngdomPersonopplysninger,
    ungdomListeFilterStandard,
    ungdomListeFilterBrukt,
    setStandardUngdomListeFilter,
    lagreNyUngdom,
    nyUngdom,
    nyUngdomFeilmelding,
    oppdaterSaksbehandler,
    settHurtigvalg,
    slettHurtigvalg,
    hurtigvalgte,
    isHurtigvalg,
    ungdommerOppdatert,
    hentDelteUngdommer,
    brukerHarTilgangTilDeltUngdom,
    brukerHarTilgangTilUngdom,
    erstattDeling,
    delteUngdommer,
    setSkolearFraHurtigvalg,
    apneHistorikk,
    lukkHistorikk,
    systemGeneratedJournalCount,
    slettUngdom
  }
})
